import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Card, CardContent, Stack, Typography, Chip, TextField, Button,
  IconButton, Tooltip, CircularProgress, Alert, Avatar, Grid, Divider,
  Tabs, Tab, LinearProgress, useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  HowToReg as RegisterIcon,
  CheckCircle as PresentIcon,
  Cancel as AbsentIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  DoneAll as AllPresentIcon,
  EventAvailable as DayIcon,
  Insights as SummaryIcon,
  Female as FemaleIcon,
  Male as MaleIcon,
  Groups as RollIcon,
  Schedule as TimeIcon,
  Lock as LockIcon,
  FreeBreakfast as NoSchoolIcon,
  ChevronLeft as PrevDayIcon,
  ChevronRight as NextDayIcon,
  Today as TodayIcon,
  EditCalendar as CatchUpIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import API from '../BaseURL';
import swal from '../utils/swal';
import { printTermAttendance } from './termAttendancePrint';
import useViewAs from './useViewAs';

// ─────────────────────────────────────────────────────────────────
// Teacher Portal › Daily Register
//
// The shape of this screen comes from how the roll is actually called
// in the classroom, not from how the data is stored:
//
//   • EVERYONE STARTS PRESENT. On a normal day almost the whole class
//     is in, so making the teacher confirm 30 present learners to flag
//     2 absentees is 30 taps of pure friction. They tap the absentees
//     and press Save.
//   • GIRLS FIRST, then boys — the order every paper register in the
//     school is already written in, so the eye finds a name where it
//     expects to.
//   • THE TOTALS LIVE AT THE TOP, next to the date, the time the roll
//     was called, the term and the year. That block is the header of
//     the paper register, so it is the header here.
//   • FOUR NAMES ACROSS on a wide screen. One name per row would mean
//     scrolling through most of a screen to reach the last learner
//     while three-quarters of the width sat empty.
//
// Two tabs: today's sheet, and what the term adds up to.
// ─────────────────────────────────────────────────────────────────

const TNUM = { fontVariantNumeric: 'tabular-nums' };

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const isFemale = (g) => String(g || '').trim().toLowerCase().startsWith('f');

// Move an ISO date by whole days, from the PARTS.
//
// `new Date('2026-08-29')` parses as UTC midnight, so anywhere behind UTC
// it already reports the previous day — step back a week from that and
// you land eight days out, on the wrong side of a weekend. The backend
// walks dates the same way for the same reason.
const shiftISO = (iso, days) => {
  const [y, m, d] = String(iso).split('-').map(Number);
  if (!y || !m || !d) return iso;
  const dt = new Date(y, m - 1, d + days);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`
       + `-${String(dt.getDate()).padStart(2, '0')}`;
};

const dowOf = (iso) => {
  const [y, m, d] = String(iso).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d).getDay();
};

const prettyTerm = (t) => String(t || '').replace(/_/g, ' ');

// "08:14 am" from whatever MySQL handed back.
const fmtTime = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const fmtDay = (iso) => {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' });
};

// A key on the keyboard, drawn as one. Teachers do not read "press the
// spacebar" in a caption; they recognise a key.
function Kbd({ children }) {
  return (
    <Box component="kbd" sx={{
      display: 'inline-block', px: 0.6, py: 0.05, mx: 0.15,
      fontSize: 10.5, fontWeight: 800, lineHeight: 1.5,
      fontFamily: 'inherit', color: 'text.primary',
      border: '1px solid', borderColor: 'divider', borderBottomWidth: 2,
      borderRadius: 0.8, bgcolor: 'background.paper',
    }}>
      {children}
    </Box>
  );
}

// ── One number in the strip across the top ────────────────────────
// Deliberately squat: four of these plus the roll have to sit on one
// line on a laptop and wrap to two on a phone, without ever becoming
// the tallest thing on the screen.
function Stat({ icon, label, value, tone, dim }) {
  return (
    <Box sx={{
      flex: '1 1 0', minWidth: 84,
      px: { xs: 1, md: 1.4 }, py: { xs: 0.7, md: 0.9 },
      borderRadius: 2,
      bgcolor: (t) => alpha(t.palette[tone].main, dim ? 0.06 : 0.12),
      border: '1px solid',
      borderColor: (t) => alpha(t.palette[tone].main, dim ? 0.15 : 0.3),
      display: 'flex', alignItems: 'center', gap: 1,
    }}>
      <Box sx={{ color: `${tone}.main`, display: 'flex' }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{
          fontSize: { xs: 17, md: 20 }, fontWeight: 900, lineHeight: 1,
          color: `${tone}.dark`, ...TNUM,
        }}>
          {value}
        </Typography>
        <Typography noWrap sx={{
          fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
          textTransform: 'uppercase', color: 'text.secondary',
        }}>
          {label}
        </Typography>
      </Box>
    </Box>
  );
}

// ── One learner on the sheet ──────────────────────────────────────
// The whole row is the control. Teachers mark this on a phone with a
// thumb; a 20px checkbox would be a miss half the time.
//
// `status` is passed in from the LIVE marks map, not read off the
// student record. That distinction is the whole bug this cell used to
// have: `s.status` is what the server last saved, so tapping a name
// moved the totals at the top while the row itself sat there still
// showing a green tick. The teacher could not tell what they had
// actually marked — which is the one thing a register has to tell them.
//
// Both states are now stated positively. "Plain row = present, coloured
// row = absent" asks the reader to notice an absence of colour; a row
// that says PRESENT and a row that says ABSENT do not.
const NameCell = forwardRef(function NameCell(
  { n, s, status, onToggle, disabled, tabStop, ring, onFocus }, ref
) {
  const absent = status === 'A';
  const tone = absent ? 'error' : 'success';
  return (
    <Box
      ref={ref}
      role="checkbox"
      aria-checked={!absent}
      aria-label={`${n}. ${s.name}, ${absent ? 'absent' : 'present'}`}
      // Roving tabindex: one stop for the whole sheet, then the arrow
      // keys move within it. Twenty tab stops to reach the last learner
      // would be worse than no keyboard support at all.
      tabIndex={disabled ? -1 : (tabStop ? 0 : -1)}
      onFocus={onFocus}
      onClick={disabled ? undefined : onToggle}
      sx={{
        display: 'flex', alignItems: 'center', gap: 0.8,
        px: 1, py: 0.55, borderRadius: 1.5,
        cursor: disabled ? 'default' : 'pointer',
        userSelect: 'none',
        border: '1px solid',
        borderColor: (t) => alpha(t.palette[tone].main, absent ? 0.55 : 0.35),
        bgcolor: (t) => alpha(t.palette[tone].main, absent ? 0.12 : 0.06),
        outline: 'none',
        // The focused learner has to be obvious from across a desk —
        // this is what tells the teacher which name the spacebar will hit.
        boxShadow: (t) => ring
          ? `0 0 0 2px ${t.palette.background.paper}, 0 0 0 4px ${t.palette.primary.main}`
          : 'none',
        transition: 'background-color .12s, border-color .12s, box-shadow .12s',
        '&:hover': disabled ? {} : {
          borderColor: (t) => t.palette[tone].main,
          bgcolor: (t) => alpha(t.palette[tone].main, absent ? 0.18 : 0.12),
        },
      }}
    >
      <Typography sx={{
        width: 20, flexShrink: 0, textAlign: 'right',
        fontSize: 11, fontWeight: 700, color: 'text.disabled', ...TNUM,
      }}>
        {n}
      </Typography>
      <Typography noWrap title={s.name} sx={{
        flex: 1, minWidth: 0, fontSize: { xs: 13, md: 13.5 },
        fontWeight: absent ? 700 : 500,
        color: absent ? 'error.dark' : 'text.primary',
        textDecoration: absent ? 'line-through' : 'none',
        textDecorationColor: (t) => alpha(t.palette.error.main, 0.55),
      }}>
        {s.name}
        {s.off_roll && (
          <Typography component="span" sx={{ fontSize: 10, color: 'text.disabled', ml: 0.6 }}>
            (off roll)
          </Typography>
        )}
      </Typography>
      {/* Icon AND word. The icon alone was ambiguous at a glance — a
          tick and a cross of the same size read as "a mark" long before
          they read as which mark. */}
      <Stack direction="row" alignItems="center" spacing={0.4}
        sx={{ flexShrink: 0, color: `${tone}.main` }}>
        {absent
          ? <AbsentIcon  sx={{ fontSize: 18 }} />
          : <PresentIcon sx={{ fontSize: 18 }} />}
        <Typography sx={{
          fontSize: 9.5, fontWeight: 900, letterSpacing: 0.5,
          display: { xs: 'none', sm: 'block' },
        }}>
          {absent ? 'ABSENT' : 'PRESENT'}
        </Typography>
      </Stack>
    </Box>
  );
});

// A labelled block of names — "Girls (13)" then "Boys (9)".
//
// `startAt` doubles as the offset into the parent's flat, keyboard-
// navigable ordering, so the number printed beside a name is also the
// position the arrow keys count in. The two must not drift apart: the
// teacher reads "12" on screen and expects twelve presses to get there.
function Section({
  title, tone, rows, startAt, marks, onToggle, disabled,
  focusIdx, sheetFocused, registerRef, onCellFocus,
}) {
  if (!rows.length) return null;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.7 }}>
        <Typography sx={{
          fontSize: 11, fontWeight: 800, letterSpacing: 1,
          textTransform: 'uppercase', color: `${tone}.main`,
        }}>
          {title}
        </Typography>
        <Chip size="small" label={rows.length}
          sx={{ height: 18, fontSize: 10.5, fontWeight: 800 }} />
        <Divider sx={{ flex: 1 }} />
      </Stack>
      <Grid container spacing={0.8}>
        {rows.map((s, i) => {
          const idx = startAt - 1 + i;
          return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={s.student_id}>
              <NameCell
                ref={(el) => registerRef(idx, el)}
                n={startAt + i}
                s={s}
                status={marks[s.student_id] || 'P'}
                disabled={disabled}
                // The tab stop follows the cursor even when focus is
                // elsewhere on the page, so Tab lands back where the
                // teacher left off. The ring only shows once focus is
                // genuinely in the sheet.
                tabStop={focusIdx === idx}
                ring={sheetFocused && focusIdx === idx}
                onFocus={() => onCellFocus(idx)}
                onToggle={() => onToggle(s.student_id)}
              />
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}

export default function TeacherPortalRegister() {
  // An admin previewing a teacher's portal — see useViewAs.js.
  const { viewAsParams } = useViewAs();
  const theme = useTheme();
  // The view switch is a rail down the left side from md up, and a
  // full-width strip below it — a fixed rail on a phone would take
  // nearly half the width the names need. The break is at md rather
  // than sm because a tablet in portrait is wide enough for the sheet
  // but not for the sheet AND a rail beside it.
  const isNarrow = useMediaQuery(theme.breakpoints.down('md'));

  const [tab, setTab]           = useState(0);
  const [boot, setBoot]         = useState(true);
  const [error, setError]       = useState('');
  const [meta, setMeta]         = useState(null);   // /register/me
  const [cla, setCla]           = useState('');
  const [date, setDate]         = useState(todayISO());

  const [sheet, setSheet]       = useState(null);   // /register/roster
  const [marks, setMarks]       = useState({});     // student_id -> 'P' | 'A'
  const [loadingSheet, setLoadingSheet] = useState(false);
  const [saving, setSaving]     = useState(false);

  const [summary, setSummary]   = useState(null);
  const [loadingSum, setLoadingSum] = useState(false);

  // Recent markable days for this class, newest first — the catch-up strip.
  const [days, setDays]               = useState([]);
  const [loadingDays, setLoadingDays] = useState(false);

  // What was on the sheet when it loaded, so Save can be disabled
  // until something actually changes.
  const baseline = useRef({});

  // ── Which classes am I on register duty for? ───────────────────
  const loadMeta = useCallback(async () => {
    setBoot(true); setError('');
    try {
      // `view_as` travels too, so an admin previewing a teacher sees
      // THAT teacher's register duty. Without it a super-admin's own
      // see-every-class privilege filled the preview. See useViewAs.js.
      const r = await API.get('/register/me', { params: { date, ...(viewAsParams || {}) } });
      setMeta(r.data || null);
      const list = r.data?.classes || [];
      setCla(prev => (prev && list.some(c => c.cla === prev)) ? prev : (list[0]?.cla || ''));
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Could not load your register classes');
    } finally {
      setBoot(false);
    }
    // `date` is intentionally in the deps: the class chips show whether
    // THAT day is already marked, so changing the date refreshes them.
  }, [date, viewAsParams]);

  useEffect(() => { loadMeta(); }, [loadMeta]);

  // ── The sheet for (class, date) ────────────────────────────────
  const loadSheet = useCallback(async () => {
    if (!cla) { setSheet(null); return; }
    setLoadingSheet(true); setError('');
    try {
      const r = await API.get('/register/roster', { params: { cla, date } });
      const data = r.data || {};
      setSheet(data);
      const m = {};
      for (const s of data.students || []) m[s.student_id] = s.status || 'P';
      setMarks(m);
      baseline.current = { ...m };
    } catch (e) {
      setSheet(null);
      setError(e?.response?.data?.message || e.message || 'Could not open that register');
    } finally {
      setLoadingSheet(false);
    }
  }, [cla, date]);

  useEffect(() => { loadSheet(); }, [loadSheet]);

  // ── The catch-up strip ─────────────────────────────────────────
  // The recent days this class could have been marked, each flagged
  // with whether it actually was. Generated server-side because only
  // the server knows both the school week and the term window; a
  // browser guessing at either would offer days the save then refuses.
  const loadDays = useCallback(async () => {
    if (!cla) { setDays([]); return; }
    setLoadingDays(true);
    try {
      const r = await API.get('/register/days', { params: { cla, limit: 14 } });
      setDays(r.data?.days || []);
    } catch {
      // Never let the strip take the sheet down with it — a teacher can
      // still mark today without knowing what they missed last Tuesday.
      setDays([]);
    } finally { setLoadingDays(false); }
  }, [cla]);

  useEffect(() => { loadDays(); }, [loadDays]);

  // ── Term totals, loaded only when that tab is opened ───────────
  const loadSummary = useCallback(async () => {
    if (!cla) { setSummary(null); return; }
    setLoadingSum(true);
    try {
      const r = await API.get('/register/summary', { params: { cla } });
      setSummary(r.data || null);
    } catch (e) {
      setSummary(null);
      setError(e?.response?.data?.message || e.message || 'Could not load the term summary');
    } finally {
      setLoadingSum(false);
    }
  }, [cla]);

  useEffect(() => { if (tab === 1) loadSummary(); }, [tab, loadSummary]);

  // ── Derived ────────────────────────────────────────────────────
  // Memoised so the empty-array fallback doesn't get a fresh identity
  // every render and re-run every memo below it on each keystroke.
  const students = useMemo(() => sheet?.students || [], [sheet]);
  const girls = useMemo(() => students.filter(s =>  isFemale(s.gender)), [students]);
  const boys  = useMemo(() => students.filter(s => !isFemale(s.gender)), [students]);

  // Counted from what is on screen, not from what was saved — the
  // numbers at the top move as the teacher taps, before any save.
  const totals = useMemo(() => {
    const t = { present_m: 0, present_f: 0, absent_m: 0, absent_f: 0 };
    for (const s of students) {
      const f = isFemale(s.gender);
      if (marks[s.student_id] === 'A') { if (f) t.absent_f++;  else t.absent_m++; }
      else                             { if (f) t.present_f++; else t.present_m++; }
    }
    return t;
  }, [students, marks]);

  const present = totals.present_f + totals.present_m;
  const absent  = totals.absent_f  + totals.absent_m;
  const roll    = students.length;

  const dirty = useMemo(
    () => students.some(s => (marks[s.student_id] || 'P') !== (baseline.current[s.student_id] || 'P')),
    [students, marks]
  );

  const toggle = (id) =>
    setMarks(prev => ({ ...prev, [id]: prev[id] === 'A' ? 'P' : 'A' }));

  const allPresent = () => {
    const m = {};
    for (const s of students) m[s.student_id] = 'P';
    setMarks(m);
  };

  // ── Keyboard control of the sheet ──────────────────────────────
  //
  // Calling a roll of thirty from a keyboard should be thirty presses,
  // not thirty aimed clicks. Arrow keys walk the list in the order the
  // numbers are printed — girls then boys, 1..n — and space marks the
  // learner under the cursor. That is the paper motion: run a finger
  // down the column, stop at the gaps.
  const ordered = useMemo(() => [...girls, ...boys], [girls, boys]);
  const cellRefs = useRef([]);
  const [focusIdx, setFocusIdx] = useState(0);
  // Whether focus is actually inside the sheet. Without this the ring
  // would sit on learner 1 from the moment the page loaded, promising a
  // spacebar that would do nothing.
  const [sheetFocused, setSheetFocused] = useState(false);

  const registerRef = useCallback((idx, el) => { cellRefs.current[idx] = el; }, []);

  // A new class or a new date is a new list; leaving the cursor at
  // position 24 of a class of 12 would put focus nowhere. Unmounted
  // cells leave null holes behind, so the ref array is trimmed to the
  // real roll rather than trusted for its length.
  useEffect(() => {
    cellRefs.current.length = ordered.length;
    setFocusIdx(0);
  }, [cla, date, ordered.length]);

  const focusCell = useCallback((i) => {
    const n = ordered.length;
    if (!n) return;
    // Clamped, not wrapped. Wrapping from the last name back to the
    // first would silently move a teacher to the top of the sheet
    // mid-roll, and they would mark the wrong learner.
    const next = Math.max(0, Math.min(i, n - 1));
    setFocusIdx(next);
    cellRefs.current[next]?.focus();
  }, [ordered.length]);

  const onSheetKeyDown = (e) => {
    if (saving || !ordered.length) return;
    switch (e.key) {
      case 'ArrowDown': case 'ArrowRight':
        e.preventDefault(); focusCell(focusIdx + 1); break;
      case 'ArrowUp': case 'ArrowLeft':
        e.preventDefault(); focusCell(focusIdx - 1); break;
      case 'Home':
        e.preventDefault(); focusCell(0); break;
      case 'End':
        e.preventDefault(); focusCell(ordered.length - 1); break;
      case ' ': case 'Spacebar': case 'Enter': {
        // preventDefault or the page scrolls a screen on every mark.
        e.preventDefault();
        const s = ordered[focusIdx];
        if (s) toggle(s.student_id);
        break;
      }
      default: break;
    }
  };

  const save = async () => {
    if (!cla || !students.length) return;
    setSaving(true);
    try {
      const r = await API.post('/register/mark', {
        cla, date,
        marks: students.map(s => ({ student_id: s.student_id, status: marks[s.student_id] || 'P' })),
      });
      baseline.current = { ...marks };
      // Refresh the header (marked_at, who marked it), the class chips,
      // and the catch-up strip — the day just saved has to stop showing
      // as outstanding the moment it is done.
      await Promise.all([loadSheet(), loadMeta(), loadDays()]);
      swal.success(r.data?.message || 'Register saved');
    } catch (e) {
      swal.error(swal.pickError(e, 'Could not save the register'));
    } finally {
      setSaving(false);
    }
  };

  // Never let a teacher open a date the backend will refuse: no future
  // days, and nothing outside the term the school is currently in.
  const maxDate = useMemo(() => {
    const t = todayISO();
    const end = meta?.end_dat || sheet?.end_dat || '';
    return end && end < t ? end : t;
  }, [meta, sheet]);
  const minDate = meta?.s_dat || sheet?.s_dat || '';

  // A school that has finished a term but not yet opened the next one
  // would otherwise land on today's date and be refused on every save.
  // Open on the last day the term actually covers instead, and say so.
  const outsideTerm = !!meta?.acc_yer && (
    (!!minDate && date < minDate) || (!!maxDate && date > maxDate)
  );
  useEffect(() => {
    if (!meta?.acc_yer) return;
    if (maxDate && date > maxDate) setDate(maxDate);
    else if (minDate && date < minDate) setDate(minDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta, maxDate, minDate]);

  // The server decides this, not the browser — same flag Examination
  // and Collections carry, so all three lock on the same rule.
  //
  // `termClosed` deliberately excludes the non-school-day refusal. A
  // Saturday is not a closed term: it shuts one date, not the screen,
  // and the teacher has to keep the date controls to get off it.
  const offDay     = meta?.period_block_kind === 'non_school_day';
  const termClosed = meta?.period_open === false && !offDay;

  // ── Moving between days ────────────────────────────────────────
  //
  // Which days the register runs on, straight from the server's own
  // rules (Settings → Academics → School Week), indexed 0 = Sunday.
  // Absent or unconfigured means every day, matching the backend's
  // "refusing is the change" default.
  const weekDays = meta?.school_week?.register;
  const isWorkDay = useCallback((iso) => {
    if (!Array.isArray(weekDays)) return true;
    const i = dowOf(iso);
    return i === null ? true : weekDays[i] !== false;
  }, [weekDays]);

  // Step to the next/previous day the register actually runs on, never
  // out of the term. Nudging a plain date field one day at a time is how
  // a teacher ends up sitting on a Sunday wondering why the sheet is
  // gone — so the arrows skip the days the school does not mark.
  const stepDay = useCallback((dir) => {
    let c = date;
    for (let i = 0; i < 400; i++) {
      c = shiftISO(c, dir);
      if (minDate && c < minDate) return;
      if (maxDate && c > maxDate) return;
      if (isWorkDay(c)) { setDate(c); return; }
    }
  }, [date, minDate, maxDate, isWorkDay]);

  const canStepBack = useMemo(() => {
    let c = date;
    for (let i = 0; i < 400; i++) {
      c = shiftISO(c, -1);
      if (minDate && c < minDate) return false;
      if (isWorkDay(c)) return true;
    }
    return false;
  }, [date, minDate, isWorkDay]);

  const canStepFwd = useMemo(() => {
    let c = date;
    for (let i = 0; i < 400; i++) {
      c = shiftISO(c, 1);
      if (maxDate && c > maxDate) return false;
      if (isWorkDay(c)) return true;
    }
    return false;
  }, [date, maxDate, isWorkDay]);

  // ── States before the sheet can render ─────────────────────────
  if (boot) {
    return <Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress size={26} /></Box>;
  }
  if (error && !meta) {
    return <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>;
  }
  // NOTE: a non-school day deliberately does NOT return early here.
  //
  // It used to. The whole screen — date field included — was replaced by
  // "No register on a Saturday", which left a teacher who had landed on
  // a Saturday with no control on the page capable of getting them off
  // it. The refusal is about ONE DATE, so it belongs where the sheet
  // would be, underneath a date bar that still works. See `offDay`.
  if (!meta?.acc_yer) {
    return (
      <Alert severity="warning" sx={{ borderRadius: 2 }}>
        No active academic year. The register is stamped with the year and term the
        school is in, so one has to be set in Examination Settings first.
      </Alert>
    );
  }
  // Term shut — the whole screen is closed, not just saving.
  //
  // The hub already greys the card out, so this is what a typed URL or
  // an old bookmark lands on. Showing the roster read-only was tempting,
  // but a register you cannot mark and cannot correct is a sheet that
  // invites a teacher to start tapping and then refuses them; the same
  // reasoning locks the Collections day sheet.
  if (termClosed) {
    return (
      <Card sx={{ borderRadius: 3, p: { xs: 3, md: 5 }, textAlign: 'center' }}>
        <LockIcon sx={{ fontSize: 48, color: 'warning.main', mb: 1 }} />
        <Typography variant="h6" fontWeight={800} sx={{ mb: 0.5 }}>
          The register is closed for this term
        </Typography>
        <Typography variant="body2" color="text.secondary"
          sx={{ mb: 2, maxWidth: 560, mx: 'auto' }}>
          {meta.period_message
            || 'The current term has ended, so no register can be taken or corrected.'}
        </Typography>
        <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap>
          <Chip size="small" variant="outlined" sx={{ fontWeight: 700 }}
            label={`${prettyTerm(meta.term)} · ${meta.acc_yer}`} />
          {!!(meta.classes || []).length && (
            <Chip size="small" variant="outlined" sx={{ fontWeight: 700 }}
              label={`${meta.classes.length} class${meta.classes.length === 1 ? '' : 'es'} assigned to you`} />
          )}
          <Button size="small" startIcon={<RefreshIcon />} onClick={loadMeta}
            sx={{ textTransform: 'none', fontWeight: 700 }}>
            Check again
          </Button>
        </Stack>
      </Card>
    );
  }
  if (!(meta.classes || []).length) {
    return (
      <Card sx={{ borderRadius: 3, p: { xs: 3, md: 5 }, textAlign: 'center' }}>
        <RegisterIcon sx={{ fontSize: 54, color: 'text.disabled', mb: 1.5 }} />
        <Typography variant="h6" fontWeight={800} sx={{ mb: 0.5 }}>
          No class assigned to you for the register
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Your school admin decides who marks each class on the Register Assignment
          page. Once a class is yours for {meta.acc_yer}, it appears here.
        </Typography>
      </Card>
    );
  }

  const classes = meta.classes || [];
  const thisClass = classes.find(c => c.cla === cla) || null;

  return (
    <Box>
      {/* ── Header: identity of the register ─────────────────────
          Class, date, year, term and the time the roll was called —
          the same block that heads the paper register, and the reason
          a teacher can tell at a glance which sheet is open. */}
      <Card sx={{ borderRadius: 3, mb: 1.5, overflow: 'visible' }}>
        <CardContent sx={{ py: { xs: 1.2, md: 1.5 }, '&:last-child': { pb: { xs: 1.2, md: 1.5 } } }}>
          <Stack direction="row" alignItems="center" spacing={1.2} flexWrap="wrap"
            useFlexGap sx={{ mb: 1.2 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 34, height: 34 }}>
              <RegisterIcon fontSize="small" />
            </Avatar>
            <Box sx={{ minWidth: 130 }}>
              <Typography variant="h6" fontWeight={900} lineHeight={1.1}
                sx={{ fontSize: { xs: 17, md: 19 } }}>
                Daily Register
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {meta.acc_yer} · {prettyTerm(meta.term) || 'no term set'}
                {thisClass?.class_t ? ` · ${thisClass.class_t}` : ''}
              </Typography>
            </Box>

            <Box sx={{ flex: 1 }} />

            {/* Date navigator. The arrows skip straight over the days
                the register does not run on, so stepping back from a
                Monday lands on Friday rather than stranding the teacher
                on a Sunday with an empty screen. */}
            <Stack direction="row" alignItems="center" spacing={0.4}>
              <Tooltip title={canStepBack ? 'Previous register day' : 'Start of the term'}>
                <span>
                  <IconButton size="small" onClick={() => stepDay(-1)}
                    disabled={!canStepBack || saving}>
                    <PrevDayIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <TextField
                size="small" type="date" label="Date"
                value={date}
                onChange={(e) => setDate(e.target.value || todayISO())}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: minDate || undefined, max: maxDate }}
                sx={{ width: 168 }}
              />
              <Tooltip title={canStepFwd ? 'Next register day' : 'Nothing later to mark'}>
                <span>
                  <IconButton size="small" onClick={() => stepDay(1)}
                    disabled={!canStepFwd || saving}>
                    <NextDayIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Back to today">
                <span>
                  <IconButton size="small" onClick={() => setDate(todayISO())}
                    disabled={date === todayISO() || saving}>
                    <TodayIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
            <Tooltip title="Reload this register">
              <span>
                <IconButton size="small" onClick={loadSheet} disabled={loadingSheet || saving}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Button
              variant="contained" size="small"
              startIcon={saving ? <CircularProgress size={15} color="inherit" /> : <SaveIcon />}
              disabled={saving || loadingSheet || !roll || outsideTerm || offDay
                        || (!dirty && sheet?.marked)}
              onClick={save}
              sx={{ fontWeight: 800, minWidth: 108 }}
            >
              {saving ? 'Saving…' : sheet?.marked ? 'Update' : 'Save register'}
            </Button>
          </Stack>

          {/* Catch-up strip — the last fortnight of markable days, each
              saying whether the roll was taken. "I did not mark Tuesday"
              is the ordinary case, and this is what makes Tuesday
              findable instead of leaving it unmarked forever. Reads
              left-to-right like a calendar, today at the right. */}
          {days.length > 0 && (
            <Stack direction="row" alignItems="center" spacing={0.8} sx={{ mb: 1.2 }}>
              <Tooltip title={`The register runs ${meta.working_days || 'every day'}`}>
                <Stack direction="row" alignItems="center" spacing={0.5}
                  sx={{ flexShrink: 0, color: 'text.secondary' }}>
                  <CatchUpIcon sx={{ fontSize: 15 }} />
                  <Typography sx={{
                    fontSize: 9.5, fontWeight: 800, letterSpacing: 0.5,
                    textTransform: 'uppercase', display: { xs: 'none', sm: 'block' },
                  }}>
                    Recent days
                  </Typography>
                </Stack>
              </Tooltip>
              <Stack direction="row" spacing={0.6} sx={{
                overflowX: 'auto', flex: 1, pb: 0.4,
                '&::-webkit-scrollbar': { height: 5 },
              }}>
                {[...days].reverse().map((d) => {
                  const selected = d.date === date;
                  return (
                    <Tooltip key={d.date} title={
                      d.marked
                        ? `${d.day_name} ${d.date} — marked: ${d.present} present, ${d.absent} absent`
                        : `${d.day_name} ${d.date} — not marked yet`
                    }>
                      <Chip
                        size="small"
                        onClick={() => setDate(d.date)}
                        variant={selected ? 'filled' : 'outlined'}
                        color={selected ? 'primary' : (d.marked ? 'success' : 'warning')}
                        icon={d.marked
                          ? <PresentIcon sx={{ fontSize: 13 }} />
                          : <TimeIcon sx={{ fontSize: 13 }} />}
                        label={`${String(d.day_name).slice(0, 3)} ${Number(d.date.slice(8, 10))}`}
                        sx={{
                          height: 24, fontWeight: 800, fontSize: 11, flexShrink: 0,
                          cursor: 'pointer',
                          // An unmarked day is the thing the teacher is
                          // hunting for, so it must not look like the
                          // quiet option.
                          ...(!d.marked && !selected
                            ? { borderStyle: 'dashed', borderWidth: 1.5 }
                            : {}),
                        }}
                      />
                    </Tooltip>
                  );
                })}
                {loadingDays && <CircularProgress size={14} sx={{ ml: 0.5, alignSelf: 'center' }} />}
              </Stack>
            </Stack>
          )}

          {/* Class picker. Chips rather than a dropdown: most teachers
              hold one or two classes, and a chip can carry the "already
              marked" tick that tells them what is still outstanding. */}
          <Stack direction="row" spacing={0.8} sx={{
            overflowX: 'auto', pb: 0.5, mb: 1.2,
            '&::-webkit-scrollbar': { height: 5 },
          }}>
            {classes.map(c => {
              const on = c.cla === cla;
              return (
                <Chip
                  key={c.cla}
                  size="small"
                  clickable
                  onClick={() => setCla(c.cla)}
                  color={on ? 'primary' : 'default'}
                  variant={on ? 'filled' : 'outlined'}
                  icon={c.marked ? <PresentIcon sx={{ fontSize: 15 }} /> : undefined}
                  label={`${c.cla} · ${c.total}`}
                  sx={{ fontWeight: on ? 800 : 600, flexShrink: 0 }}
                />
              );
            })}
          </Stack>

          {/* The four numbers, plus the roll. Present is shown first
              because that is the figure the head asks for. */}
          <Stack direction="row" spacing={{ xs: 0.8, md: 1.2 }} flexWrap="wrap" useFlexGap>
            <Stat tone="success" label="Girls present" value={totals.present_f}
              icon={<FemaleIcon sx={{ fontSize: 20 }} />} />
            <Stat tone="success" label="Boys present" value={totals.present_m}
              icon={<MaleIcon sx={{ fontSize: 20 }} />} />
            <Stat tone="error" label="Girls absent" value={totals.absent_f} dim={!totals.absent_f}
              icon={<FemaleIcon sx={{ fontSize: 20 }} />} />
            <Stat tone="error" label="Boys absent" value={totals.absent_m} dim={!totals.absent_m}
              icon={<MaleIcon sx={{ fontSize: 20 }} />} />
            <Stat tone="primary" label={`On roll · ${present} in`} value={`${present}/${roll}`}
              icon={<RollIcon sx={{ fontSize: 20 }} />} />
          </Stack>

          {/* When it was taken, and by whom. Empty until it is saved. */}
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap
            sx={{ mt: 1 }}>
            <Chip size="small" variant="outlined" icon={<DayIcon sx={{ fontSize: 15 }} />}
              label={fmtDay(date)} sx={{ fontWeight: 700 }} />
            {sheet?.marked ? (
              <Chip size="small" color="success" variant="outlined"
                icon={<TimeIcon sx={{ fontSize: 15 }} />}
                label={`Taken ${fmtTime(sheet.marked_at)}${sheet.marked_by_name ? ` · ${sheet.marked_by_name}` : ''}`}
                sx={{ fontWeight: 700 }} />
            ) : (
              <Chip size="small" color="warning" variant="outlined"
                icon={<TimeIcon sx={{ fontSize: 15 }} />}
                label="Not taken yet" sx={{ fontWeight: 700 }} />
            )}
            {dirty && (
              <Chip size="small" color="info" label="Unsaved changes" sx={{ fontWeight: 700 }} />
            )}
          </Stack>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>{error}</Alert>}


      {/* ── The two views ────────────────────────────────────────
          The switch is a RAIL DOWN THE LEFT, not a strip across the
          top. Two tabs stretched over a 1900px header read as a
          fragment of a heading rather than as a control — there is
          nothing to their right to make them look like a choice — and
          on this screen the eye is already travelling down the left
          edge, along the numbered names.

          Below md it goes back to a full-width strip: a 170px rail out
          of a 390px phone is nearly half the width the register needs
          for names. */}
      <Card sx={{ borderRadius: 3 }}>
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'stretch',
        }}>
          <Tabs
            value={tab} onChange={(_, v) => setTab(v)}
            orientation={isNarrow ? 'horizontal' : 'vertical'}
            variant={isNarrow ? 'fullWidth' : 'standard'}
            sx={{
              flexShrink: 0,
              width: { xs: 'auto', md: 194 },
              pt: { xs: 0, md: 1 },
              // The divider follows the rail: under the strip on a
              // phone, beside it on a laptop.
              borderBottom: { xs: '1px solid', md: 0 },
              borderRight:  { xs: 0, md: '1px solid' },
              borderColor: 'divider',
              minHeight: 42,
              '& .MuiTab-root': {
                minHeight: 42,
                fontWeight: 800,
                textTransform: 'none',
                // Left-aligned in the rail. Centred labels in a column
                // leave the icons on a ragged edge, which reads as two
                // unrelated buttons rather than one list of views.
                //
                // justifyContent only — the tab is a ROW (icon then
                // label), so alignItems is the vertical axis here and
                // flex-starting it would lift the icon off the label's
                // centre line.
                justifyContent: { xs: 'center', md: 'flex-start' },
                textAlign: { xs: 'center', md: 'left' },
                px: { xs: 1.5, md: 2 },
              },
            }}
          >
            <Tab icon={<RegisterIcon fontSize="small" />} iconPosition="start" label="Mark register" />
            <Tab icon={<SummaryIcon fontSize="small" />}  iconPosition="start" label="Term attendance" />
          </Tabs>

          {/* minWidth: 0 or the four-across grid of names refuses to
              shrink and pushes the whole card wider than the page. */}
          <CardContent sx={{
            flex: 1, minWidth: 0,
            p: { xs: 1.2, md: 1.8 }, '&:last-child': { pb: { xs: 1.5, md: 2 } },
          }}>
          {tab === 0 && (
            // One date is shut, not the screen. The date bar and the
            // catch-up strip above are still live, so this says what is
            // wrong with THIS day and leaves the teacher able to pick
            // another — which the old full-screen version did not.
            offDay ? (
              <Box sx={{ py: { xs: 3, md: 5 }, textAlign: 'center' }}>
                <NoSchoolIcon sx={{ fontSize: 44, color: 'warning.main', mb: 1 }} />
                <Typography variant="h6" fontWeight={800} sx={{ mb: 0.5 }}>
                  No register on {meta.day_name ? `a ${meta.day_name}` : 'this day'}
                </Typography>
                <Typography variant="body2" color="text.secondary"
                  sx={{ mb: 2, maxWidth: 560, mx: 'auto' }}>
                  The register runs {meta.working_days || 'on school days only'}.
                  Pick another date above, or use the arrows to jump to the
                  nearest day the school marks.
                </Typography>
                <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap>
                  <Button size="small" variant="outlined" startIcon={<PrevDayIcon />}
                    onClick={() => stepDay(-1)} disabled={!canStepBack}
                    sx={{ textTransform: 'none', fontWeight: 700 }}>
                    Previous register day
                  </Button>
                  {canStepFwd && (
                    <Button size="small" variant="outlined" endIcon={<NextDayIcon />}
                      onClick={() => stepDay(1)}
                      sx={{ textTransform: 'none', fontWeight: 700 }}>
                      Next register day
                    </Button>
                  )}
                </Stack>
              </Box>
            ) :
            loadingSheet ? (
              <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={24} /></Box>
            ) : !roll ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 5, textAlign: 'center' }}>
                No learners on the roll for {cla}.
              </Typography>
            ) : (
              <>
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap
                  sx={{ mb: 1.2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 200 }}>
                    Everyone starts <b>present</b> — tap only the learners who are away,
                    then save.
                    <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
                      {' '}Or use the keyboard: <Kbd>↑</Kbd> <Kbd>↓</Kbd> to move,{' '}
                      <Kbd>space</Kbd> to mark absent or present.
                    </Box>
                  </Typography>
                  <Button size="small" startIcon={<AllPresentIcon />} onClick={allPresent}
                    disabled={!absent} sx={{ textTransform: 'none', fontWeight: 700 }}>
                    Reset all to present
                  </Button>
                </Stack>

                {/* One key handler for the whole sheet rather than one per
                    name: the arrows move BETWEEN cells, so the sheet is
                    the thing that owns the motion. */}
                <Box
                  onKeyDown={onSheetKeyDown}
                  // React's onFocus/onBlur bubble, so the sheet can tell
                  // "focus moved between two of my cells" from "focus
                  // left me entirely" via relatedTarget — which is what
                  // keeps the ring from flickering on every arrow press.
                  onFocus={() => setSheetFocused(true)}
                  onBlur={(e) => {
                    if (!e.currentTarget.contains(e.relatedTarget)) setSheetFocused(false);
                  }}
                >
                  <Section title="Girls" tone="secondary" rows={girls} startAt={1}
                    marks={marks} onToggle={toggle} disabled={saving}
                    focusIdx={focusIdx} sheetFocused={sheetFocused}
                    registerRef={registerRef} onCellFocus={setFocusIdx}
                  />
                  <Section title="Boys" tone="primary" rows={boys} startAt={girls.length + 1}
                    marks={marks} onToggle={toggle} disabled={saving}
                    focusIdx={focusIdx} sheetFocused={sheetFocused}
                    registerRef={registerRef} onCellFocus={setFocusIdx}
                  />
                </Box>

                {/* Repeat of the count at the foot of the sheet, the way
                    a paper register is totalled at the bottom of the page. */}
                <Divider sx={{ my: 1.2 }} />
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                  <Typography sx={{ fontSize: 12, fontWeight: 800, color: 'text.secondary' }}>
                    TOTAL
                  </Typography>
                  <Chip size="small" color="success" variant="outlined" sx={{ fontWeight: 800 }}
                    label={`Present ${present} (${totals.present_f}F / ${totals.present_m}M)`} />
                  <Chip size="small" color={absent ? 'error' : 'default'} variant="outlined"
                    sx={{ fontWeight: 800 }}
                    label={`Absent ${absent} (${totals.absent_f}F / ${totals.absent_m}M)`} />
                  <Chip size="small" variant="outlined" sx={{ fontWeight: 800 }}
                    label={`On roll ${roll}`} />
                  <Box sx={{ flex: 1 }} />
                  <Button
                    variant="contained" size="small"
                    startIcon={saving ? <CircularProgress size={15} color="inherit" /> : <SaveIcon />}
                    disabled={saving || outsideTerm || (!dirty && sheet?.marked)}
                    onClick={save}
                    sx={{ fontWeight: 800 }}
                  >
                    {saving ? 'Saving…' : sheet?.marked ? 'Update register' : 'Save register'}
                  </Button>
                </Stack>
              </>
            )
          )}

          {tab === 1 && (
            loadingSum ? (
              <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={24} /></Box>
            ) : !summary ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 5, textAlign: 'center' }}>
                Nothing to summarise yet.
              </Typography>
            ) : (
              <TermSummary summary={summary} cla={cla} />
            )
          )}
          </CardContent>
        </Box>
      </Card>
    </Box>
  );
}

// ── Term attendance ───────────────────────────────────────────────
// "Out of the 30 days marked so far, this learner made 28." The
// denominator is days MARKED, never days on the calendar — a day the
// register was never taken is nobody's absence.
function TermSummary({ summary, cla }) {
  const rows  = summary.students || [];
  const days  = summary.days_marked || 0;
  const girls = rows.filter(r =>  isFemale(r.gender));
  const boys  = rows.filter(r => !isFemale(r.gender));
  const t     = summary.totals || {};
  const [printing, setPrinting] = useState(false);

  // The letterhead is fetched when Print is pressed, not on every visit
  // to this tab — most openings of the term view never print.
  const doPrint = async () => {
    setPrinting(true);
    try {
      let school = {};
      try {
        const r = await API.get('/setup/info');
        school = r.data?.info || r.data || {};
      } catch {
        // No letterhead is a worse sheet, not a failed one — print it
        // rather than refusing over the header.
      }
      if (!printTermAttendance({ summary, school, cla })) {
        swal.warning(
          'Your browser blocked the print window. Allow pop-ups for this site and try again.',
          'Pop-up blocked'
        );
      }
    } finally { setPrinting(false); }
  };

  const Row = ({ n, r }) => {
    const pct = r.pct == null ? 0 : r.pct;
    const tone = r.pct == null ? 'inherit' : pct >= 90 ? 'success' : pct >= 75 ? 'warning' : 'error';
    return (
      <Box sx={{
        px: 1, py: 0.55, borderRadius: 1.5, border: '1px solid', borderColor: 'divider',
        display: 'flex', alignItems: 'center', gap: 0.8,
      }}>
        <Typography sx={{ width: 20, textAlign: 'right', fontSize: 11, fontWeight: 700,
          color: 'text.disabled', ...TNUM }}>
          {n}
        </Typography>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography noWrap title={r.name} sx={{ fontSize: 13, fontWeight: 500 }}>
            {r.name}
          </Typography>
          <LinearProgress
            variant="determinate" value={Math.min(100, pct)}
            color={tone === 'inherit' ? 'inherit' : tone}
            sx={{ height: 3, borderRadius: 2, mt: 0.3, opacity: r.pct == null ? 0.25 : 1 }}
          />
        </Box>
        <Typography sx={{ fontSize: 12, fontWeight: 800, ...TNUM, whiteSpace: 'nowrap' }}>
          {r.present}/{r.marked || days}
        </Typography>
        <Typography sx={{
          width: 34, textAlign: 'right', fontSize: 11.5, fontWeight: 800, ...TNUM,
          color: r.pct == null ? 'text.disabled' : `${tone}.main`,
        }}>
          {r.pct == null ? '—' : `${r.pct}%`}
        </Typography>
      </Box>
    );
  };

  const Block = ({ title, tone, list, startAt }) => !list.length ? null : (
    <Box sx={{ mb: 1.5 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.7 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 1,
          textTransform: 'uppercase', color: `${tone}.main` }}>
          {title}
        </Typography>
        <Chip size="small" label={list.length} sx={{ height: 18, fontSize: 10.5, fontWeight: 800 }} />
        <Divider sx={{ flex: 1 }} />
      </Stack>
      <Grid container spacing={0.8}>
        {list.map((r, i) => (
          <Grid item xs={12} sm={6} lg={4} key={r.student_id}>
            <Row n={startAt + i} r={r} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  return (
    <>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center"
        sx={{ mb: 1.2 }}>
        <Chip size="small" color="primary" sx={{ fontWeight: 800 }}
          label={`${days} day${days === 1 ? '' : 's'} marked`} />
        <Chip size="small" variant="outlined" sx={{ fontWeight: 700 }}
          label={`${prettyTerm(summary.term)} · ${summary.acc_yer}`} />
        <Chip size="small" variant="outlined" sx={{ fontWeight: 700 }}
          label={`${summary.from} → ${summary.to}`} />
        <Box sx={{ flex: 1 }} />
        <Chip size="small" color="success" variant="outlined" sx={{ fontWeight: 800 }}
          label={`Attendances ${(t.present_f || 0) + (t.present_m || 0)}`} />
        <Chip size="small" color="error" variant="outlined" sx={{ fontWeight: 800 }}
          label={`Absences ${(t.absent_f || 0) + (t.absent_m || 0)}`} />
        {/* Parents and the district ask for this on paper. Disabled
            until there is something to print — a sheet of dashes helps
            nobody and looks like a fault. */}
        <Tooltip title={days
          ? 'Print the term attendance report'
          : 'Nothing marked yet this term'}>
          <span>
            <Button size="small" variant="outlined" startIcon={<PrintIcon />}
              onClick={doPrint} disabled={!days || printing}
              sx={{ textTransform: 'none', fontWeight: 800 }}>
              {printing ? 'Preparing…' : 'Print'}
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {!days ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 5, textAlign: 'center' }}>
          This class has not been marked yet this term. Take today's register and the
          totals start building here.
        </Typography>
      ) : (
        <>
          {/* One line per marked day — a term of registers reads across
              in a single strip instead of down a page. */}
          <Stack direction="row" spacing={0.6} sx={{
            overflowX: 'auto', pb: 0.8, mb: 1.4,
            '&::-webkit-scrollbar': { height: 5 },
          }}>
            {(summary.days || []).map(d => (
              <Tooltip key={d.reg_date}
                title={`${fmtDay(d.reg_date)} — ${d.present} present, ${d.absent} absent`}>
                <Chip
                  size="small" variant="outlined"
                  color={d.absent === 0 ? 'success' : d.absent > 3 ? 'error' : 'warning'}
                  label={`${d.reg_date.slice(8)} · ${d.absent}`}
                  sx={{ flexShrink: 0, fontWeight: 700, ...TNUM }}
                />
              </Tooltip>
            ))}
          </Stack>

          <Block title="Girls" tone="secondary" list={girls} startAt={1} />
          <Block title="Boys"  tone="primary"   list={boys}  startAt={girls.length + 1} />

          <Divider sx={{ my: 1.2 }} />
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
            <Typography sx={{ fontSize: 12, fontWeight: 800, color: 'text.secondary' }}>
              TERM TOTAL
            </Typography>
            <Chip size="small" color="success" variant="outlined" sx={{ fontWeight: 800 }}
              label={`Present ${t.present_f || 0}F / ${t.present_m || 0}M`} />
            <Chip size="small" color="error" variant="outlined" sx={{ fontWeight: 800 }}
              label={`Absent ${t.absent_f || 0}F / ${t.absent_m || 0}M`} />
            <Chip size="small" variant="outlined" sx={{ fontWeight: 800 }}
              label={`${rows.length} learners over ${days} days`} />
          </Stack>
        </>
      )}
    </>
  );
}
